import React, { useState } from 'react';
import {
  Box,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Badge,
  Chip,
  TextField,
  InputAdornment,
  IconButton,
  Card,
  Skeleton,
  Alert
} from '@mui/material';
import {
  Search as SearchIcon,
  Phone as PhoneIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  FilterList as FilterIcon
} from '@mui/icons-material';
import { useChat } from '../hooks/useChat';
import { WhatsAppChat } from '../types';
import { formatDistanceToNow } from 'date-fns';
import { fr } from 'date-fns/locale';

interface ChatListProps {
  selectedChatId?: string;
  onChatSelect: (chatId: string) => void;
  onNewChat?: () => void;
}

const ChatList: React.FC<ChatListProps> = ({
  selectedChatId,
  onChatSelect,
  onNewChat
}) => {
  const { chats, isLoading, error, hasUnreadMessages, getLastMessage } = useChat();
  const [searchTerm, setSearchTerm] = useState('');
  const [showFilters, setShowFilters] = useState(false);

  // Filtrage des chats
  const filteredChats = chats.filter(chat => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    return (
      chat.contactName?.toLowerCase().includes(searchLower) ||
      chat.phoneNumber.includes(searchTerm) ||
      getLastMessage(chat.id)?.content.toLowerCase().includes(searchLower)
    );
  });

  // Tri des chats (non lus en premier, puis par date)
  const sortedChats = [...filteredChats].sort((a, b) => {
    // D'abord les non lus
    if (a.unreadCount > 0 && b.unreadCount === 0) return -1;
    if (a.unreadCount === 0 && b.unreadCount > 0) return 1;
    
    // Puis par date de dernière activité
    return new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime();
  });

  const formatLastMessageTime = (timestamp: string) => {
    try {
      return formatDistanceToNow(new Date(timestamp), {
        addSuffix: true,
        locale: fr
      });
    } catch {
      return 'À l\'instant';
    }
  };

  const getStatusColor = (status: WhatsAppChat['status']) => {
    switch (status) {
      case 'active': return 'success';
      case 'waiting': return 'warning';
      case 'closed': return 'default';
      default: return 'default';
    }
  };

  const getChatAvatar = (chat: WhatsAppChat) => {
    const firstLetter = chat.contactName?.[0] || chat.phoneNumber[0];
    return (
      <Avatar sx={{ bgcolor: hasUnreadMessages(chat.id) ? 'primary.main' : 'grey.400' }}>
        {firstLetter.toUpperCase()}
      </Avatar>
    );
  };

  const truncateMessage = (message: string, maxLength: number = 50) => {
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Erreur lors du chargement des conversations
      </Alert>
    );
  }

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Header avec recherche */}
      <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
        <Typography variant="h6" gutterBottom>
          Conversations WhatsApp
        </Typography>
        
        <TextField
          fullWidth
          size="small"
          placeholder="Rechercher une conversation..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon color="action" />
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                <IconButton 
                  size="small"
                  onClick={() => setShowFilters(!showFilters)}
                  color={showFilters ? 'primary' : 'default'}
                >
                  <FilterIcon />
                </IconButton>
              </InputAdornment>
            )
          }}
        />

        {/* Statistiques rapides */}
        <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
          <Chip 
            size="small" 
            label={`${chats.length} total`}
            variant="outlined"
          />
          <Chip 
            size="small" 
            label={`${chats.filter(c => c.unreadCount > 0).length} non lus`}
            color="primary"
            variant={chats.some(c => c.unreadCount > 0) ? 'filled' : 'outlined'}
          />
        </Box>
      </Box>

      {/* Liste des chats */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        {isLoading ? (
          // Skeleton loading
          <List>
            {[...Array(5)].map((_, index) => (
              <ListItem key={index}>
                <ListItemAvatar>
                  <Skeleton variant="circular" width={40} height={40} />
                </ListItemAvatar>
                <ListItemText
                  primary={<Skeleton variant="text" width="60%" />}
                  secondary={<Skeleton variant="text" width="80%" />}
                />
              </ListItem>
            ))}
          </List>
        ) : sortedChats.length === 0 ? (
          // Empty state
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <PhoneIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {searchTerm ? 'Aucun résultat' : 'Aucune conversation'}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {searchTerm 
                ? 'Essayez de modifier votre recherche'
                : 'Les nouvelles conversations apparaîtront ici'
              }
            </Typography>
          </Box>
        ) : (
          // Liste des chats
          <List sx={{ p: 0 }}>
            {sortedChats.map((chat) => {
              const lastMessage = getLastMessage(chat.id);
              const isSelected = chat.id === selectedChatId;
              const isUnread = hasUnreadMessages(chat.id);
              
              return (
                <ListItem
                  key={chat.id}
                  button
                  selected={isSelected}
                  onClick={() => onChatSelect(chat.id)}
                  sx={{
                    borderLeft: isSelected ? 3 : 0,
                    borderColor: 'primary.main',
                    backgroundColor: isUnread ? 'action.hover' : 'transparent',
                    '&:hover': {
                      backgroundColor: 'action.selected'
                    }
                  }}
                >
                  <ListItemAvatar>
                    <Badge
                      badgeContent={chat.unreadCount > 0 ? chat.unreadCount : null}
                      color="error"
                      max={99}
                    >
                      {getChatAvatar(chat)}
                    </Badge>
                  </ListItemAvatar>
                  
                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <Typography 
                          variant="subtitle2" 
                          fontWeight={isUnread ? 'bold' : 'normal'}
                          sx={{ flex: 1 }}
                        >
                          {chat.contactName || chat.phoneNumber}
                        </Typography>
                        <Chip
                          size="small"
                          label={chat.status}
                          color={getStatusColor(chat.status)}
                          variant="outlined"
                          sx={{ height: 20 }}
                        />
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          fontWeight={isUnread ? 'medium' : 'normal'}
                          sx={{ 
                            display: 'flex', 
                            alignItems: 'center', 
                            gap: 0.5,
                            mb: 0.5 
                          }}
                        >
                          {lastMessage?.sender === 'ai' && <BotIcon sx={{ fontSize: 14 }} />}
                          {lastMessage?.sender === 'agent' && <PersonIcon sx={{ fontSize: 14 }} />}
                          {lastMessage ? truncateMessage(lastMessage.content) : 'Nouvelle conversation'}
                        </Typography>
                        
                        <Typography variant="caption" color="text.secondary">
                          {formatLastMessageTime(chat.updatedAt)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              );
            })}
          </List>
        )}
      </Box>
    </Card>
  );
};

export default ChatList;
